import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import _ from 'lodash';
import QuickActions from "./components/sites/QuickActions.jsx";
import SiteCharts from "./dashboard/SiteCharts.jsx";
import DashboardStats from "./dashboard/DashboardStats.jsx";
import SiteStatusWidget from "./components/sites/SiteStatusWidget.jsx";
import SitesTable from "./components/components/sites/SitesTable.jsx";
import RecentActivity from "./dashboard/RecentActivity.jsx";
import SocketStatus from "./components/SocketStatus.jsx";
import {
    Activity,
    AlertTriangle,
    BarChart3,
    Bell,
    Clock,
    Download,
    Filter,
    Globe,
    Loader2,
    Menu,
    Plus,
    RefreshCw,
    Search,
    Settings,
    Upload,
    Users,
    X
} from 'lucide-react';
import {useSiteMonitoring} from "./hooks/useSiteMonitoring.jsx";
import {useSocket} from "./dashboard/SocketContext.jsx";
import {fetchSites} from "@/store/features/sites/sitesSlice.js";

export default function Dashboard() {
    const dispatch = useDispatch();
    const sites = useSelector(state => state.sites.items);
    const loading = useSelector(state => state.sites.loading);
    const error = useSelector(state => state.sites.error);

    // Utiliser useRef pour garder une référence stable aux sites
    const sitesRef = useRef([]);

    // Extraire les fonctions du hook AVANT de les utiliser dans useEffect
    const { monitorSites, stopAllMonitoring, realtimeStats } = useSiteMonitoring();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [timeFilter, setTimeFilter] = useState('today');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [hasStartedMonitoring, setHasStartedMonitoring] = useState(false);
    const { isConnected, joinAllSites, socketService } = useSocket();

    useEffect(() => {
        if (isConnected && socketService) {
            // Rejoindre tous les sites après 2 secondes (laisser le temps à la connexion de se stabiliser)
            const timer = setTimeout(() => {
                joinAllSites().then(results => {
                    console.log('📊 Sites rejoints:', results.filter(r => r.success).length);
                });
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [isConnected, joinAllSites, socketService]);
    // Mettre à jour la ref quand les sites changent
    useEffect(() => {
        sitesRef.current = sites || [];
    }, [sites]);

    // Charger les sites au montage
    useEffect(() => {
        console.log('📥 Chargement des sites...');
        dispatch(fetchSites());
    }, [dispatch]);

    // Utiliser useMemo pour les clés de site (évite la recréation à chaque rendu)
    const siteKeys = useMemo(() => {
        if (!sites || !Array.isArray(sites)) return [];
        return sites.map(site => site.siteKey || `site-${site.id}`);
    }, [sites]);

    // Surveiller les sites - version corrigée sans boucle infinie
    useEffect(() => {
        // Vérifier si on a déjà commencé la surveillance
        if (hasStartedMonitoring) return;

        if (siteKeys.length > 0 && !hasStartedMonitoring) {
            console.log(`👁️  Démarrage surveillance pour ${siteKeys.length} sites`);

            // Marquer qu'on a commencé
            setHasStartedMonitoring(true);

            // Démarrer la surveillance
            monitorSites(siteKeys);

            // Simuler des notifications
            setNotifications([
                {
                    id: 1,
                    type: 'info',
                    title: 'Surveillance démarrée',
                    message: `Surveillance activée pour ${siteKeys.length} sites`,
                    time: 'À l\'instant',
                    read: false
                },
                {
                    id: 2,
                    type: 'success',
                    title: 'Connexion établie',
                    message: 'Connecté au serveur en temps réel',
                    time: '2 min',
                    read: true
                }
            ]);
        }

        // Nettoyage
        return () => {
            if (hasStartedMonitoring) {
                console.log('🧹 Arrêt de la surveillance');
                stopAllMonitoring();
                setHasStartedMonitoring(false);
            }
        };
    }, [siteKeys, monitorSites, stopAllMonitoring, hasStartedMonitoring]); // Dépend de siteKeys (memoized)

    // Rafraîchir manuellement avec reset de l'état de surveillance
    const handleRefresh = useCallback(() => {
        console.log('🔄 Rafraîchissement manuel');
        setHasStartedMonitoring(false);
        dispatch(fetchSites());
    }, [dispatch]);

    // Filtrer les sites avec lodash pour plus de sécurité
    const filteredSites = useMemo(() => {
        if (!_.isArray(sites) || sites.length === 0) return [];

        const lowerSearchQuery = searchQuery.toLowerCase();

        return sites.filter(site => {
            const siteName = _.get(site, 'name', '').toLowerCase();
            const siteUrl = _.get(site, 'url', '').toLowerCase();
            const siteStatus = _.get(site, 'status', '');

            const matchesSearch = siteName.includes(lowerSearchQuery) ||
                siteUrl.includes(lowerSearchQuery);

            const matchesStatus = statusFilter === 'all' || siteStatus === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [sites, searchQuery, statusFilter]);

    // Compter les sites par statut
    const statusCounts = useMemo(() => {
        const safeSites = _.isArray(sites) ? sites : [];

        return {
            all: safeSites.length,
            active: _.filter(safeSites, s => _.get(s, 'status') === 'active').length,
            warning: _.filter(safeSites, s => _.get(s, 'status') === 'warning').length,
            error: _.filter(safeSites, s => _.get(s, 'status') === 'error').length,
            offline: _.filter(safeSites, s => _.get(s, 'status') === 'inactive').length
        };
    }, [sites]);

    // Calculer les stats globales avec lodash
    const globalStats = useMemo(() => {
        const safeSites = _.isArray(sites) ? sites : [];
        const safeRealtimeStats = _.isObject(realtimeStats) ? realtimeStats : {};

        const totalVisits = _.sumBy(safeSites, site => _.toNumber(_.get(site, 'totalVisites', 0)));
        const todayVisits = _.sumBy(safeSites, site => _.toNumber(_.get(site, 'todayVisites', 0)));
        const activeSites = _.filter(safeSites, s => _.get(s, 'status') === 'active').length;

        // Calculer les visiteurs en ligne avec lodash
        const onlineNow = _.reduce(
            _.values(safeRealtimeStats),
            (sum, stat) => {
                if (_.isObject(stat)) {
                    return sum + _.toNumber(_.get(stat, 'online', 0));
                }
                return sum;
            },
            0
        );

        // Calculer l'uptime moyen
        const avgUptime = safeSites.length > 0
            ? _.meanBy(safeSites, site => _.toNumber(_.get(site, 'uptime', 0)))
            : 0;

        return {
            totalSites: safeSites.length,
            activeSites,
            totalVisits,
            todayVisits,
            onlineNow,
            avgUptime
        };
    }, [sites, realtimeStats]);

    if (loading && !sites.length) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="relative">
                        <Globe className="h-20 w-20 text-blue-500 mx-auto mb-6 animate-pulse" />
                        <Loader2 className="h-8 w-8 text-blue-600 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-spin" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Chargement du dashboard</h2>
                    <p className="text-gray-600">Initialisation de la surveillance en temps réel...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Barre de navigation supérieure */}
            {/*<nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">*/}
            {/*    <div className="px-4 sm:px-6 lg:px-8">*/}
            {/*        <div className="flex items-center justify-between h-16">*/}
            {/*            /!* Logo et menu burger *!/*/}
            {/*            <div className="flex items-center">*/}
            {/*                <button*/}
            {/*                    onClick={() => setSidebarOpen(!sidebarOpen)}*/}
            {/*                    className="p-2 rounded-lg hover:bg-gray-100 lg:hidden"*/}
            {/*                >*/}
            {/*                    {sidebarOpen ? (*/}
            {/*                        <X className="h-5 w-5 text-gray-600" />*/}
            {/*                    ) : (*/}
            {/*                        <Menu className="h-5 w-5 text-gray-600" />*/}
            {/*                    )}*/}
            {/*                </button>*/}

            {/*                <div className="flex items-center ml-4 lg:ml-0">*/}
            {/*                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">*/}
            {/*                        <Activity className="h-5 w-5 text-white" />*/}
            {/*                    </div>*/}
            {/*                    <span className="ml-3 text-xl font-bold text-gray-900 hidden sm:block">*/}
            {/*                        SiteMonitor*/}
            {/*                    </span>*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            /!* Barre de recherche centrale *!/*/}
            {/*            <div className="flex-1 max-w-2xl mx-4 hidden md:block">*/}
            {/*                <div className="relative">*/}
            {/*                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />*/}
            {/*                    <input*/}
            {/*                        type="text"*/}
            {/*                        placeholder="Rechercher un site, une IP, une localisation..."*/}
            {/*                        value={searchQuery}*/}
            {/*                        onChange={(e) => setSearchQuery(e.target.value)}*/}
            {/*                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-gray-50"*/}
            {/*                    />*/}
            {/*                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />*/}
            {/*                </div>*/}
            {/*            </div>*/}

            {/*            /!* Actions droite *!/*/}
            {/*            <div className="flex items-center space-x-3">*/}
            {/*                /!* Bouton recherche mobile *!/*/}
            {/*                <button*/}
            {/*                    className="p-2 rounded-lg hover:bg-gray-100 md:hidden"*/}
            {/*                    onClick={() => {*/}
            {/*                        // Focus sur la recherche mobile si nécessaire*/}
            {/*                        const mobileSearch = document.querySelector('#mobile-search');*/}
            {/*                        if (mobileSearch) mobileSearch.focus();*/}
            {/*                    }}*/}
            {/*                >*/}
            {/*                    <Search className="h-5 w-5 text-gray-600" />*/}
            {/*                </button>*/}

            {/*                /!* Bouton rafraîchir *!/*/}
            {/*                <button*/}
            {/*                    onClick={handleRefresh}*/}
            {/*                    disabled={loading}*/}
            {/*                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"*/}
            {/*                    title="Rafraîchir les données"*/}
            {/*                >*/}
            {/*                    <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />*/}
            {/*                </button>*/}

            {/*                /!* Notifications *!/*/}
            {/*                <div className="relative">*/}
            {/*                    <button*/}
            {/*                        onClick={() => setShowNotifications(!showNotifications)}*/}
            {/*                        className="p-2 rounded-lg hover:bg-gray-100 relative"*/}
            {/*                    >*/}
            {/*                        <Bell className="h-5 w-5 text-gray-600" />*/}
            {/*                        {notifications.filter(n => !n.read).length > 0 && (*/}
            {/*                            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>*/}
            {/*                        )}*/}
            {/*                    </button>*/}

            {/*                    {showNotifications && (*/}
            {/*                        <>*/}
            {/*                            <div*/}
            {/*                                className="fixed inset-0 z-10"*/}
            {/*                                onClick={() => setShowNotifications(false)}*/}
            {/*                            />*/}
            {/*                            <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20">*/}
            {/*                                <div className="p-4 border-b border-gray-200">*/}
            {/*                                    <h3 className="font-semibold text-gray-900">Notifications</h3>*/}
            {/*                                </div>*/}
            {/*                                <div className="max-h-96 overflow-y-auto">*/}
            {/*                                    {notifications.length > 0 ? (*/}
            {/*                                        notifications.map(notification => (*/}
            {/*                                            <div*/}
            {/*                                                key={notification.id}*/}
            {/*                                                className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${!notification.read ? 'bg-blue-50' : ''}`}*/}
            {/*                                            >*/}
            {/*                                                <div className="flex items-start">*/}
            {/*                                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center mr-3 ${*/}
            {/*                                                        notification.type === 'success' ? 'bg-green-100' :*/}
            {/*                                                            notification.type === 'warning' ? 'bg-yellow-100' :*/}
            {/*                                                                notification.type === 'error' ? 'bg-red-100' : 'bg-blue-100'*/}
            {/*                                                    }`}>*/}
            {/*                                                        <Bell className={`h-4 w-4 ${*/}
            {/*                                                            notification.type === 'success' ? 'text-green-600' :*/}
            {/*                                                                notification.type === 'warning' ? 'text-yellow-600' :*/}
            {/*                                                                    notification.type === 'error' ? 'text-red-600' : 'text-blue-600'*/}
            {/*                                                        }`} />*/}
            {/*                                                    </div>*/}
            {/*                                                    <div className="flex-1">*/}
            {/*                                                        <p className="font-medium text-gray-900">{notification.title}</p>*/}
            {/*                                                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>*/}
            {/*                                                        <p className="text-xs text-gray-400 mt-2">{notification.time}</p>*/}
            {/*                                                    </div>*/}
            {/*                                                </div>*/}
            {/*                                            </div>*/}
            {/*                                        ))*/}
            {/*                                    ) : (*/}
            {/*                                        <div className="p-8 text-center text-gray-500">*/}
            {/*                                            <Bell className="h-8 w-8 mx-auto mb-3 opacity-50" />*/}
            {/*                                            <p>Aucune notification</p>*/}
            {/*                                        </div>*/}
            {/*                                    )}*/}
            {/*                                </div>*/}
            {/*                                <div className="p-4 border-t border-gray-200">*/}
            {/*                                    <button*/}
            {/*                                        className="text-sm text-blue-600 hover:text-blue-800 w-full text-center"*/}
            {/*                                        onClick={() => {*/}
            {/*                                            setNotifications(prev => prev.map(n => ({ ...n, read: true })));*/}
            {/*                                            setShowNotifications(false);*/}
            {/*                                        }}*/}
            {/*                                    >*/}
            {/*                                        Marquer toutes comme lues*/}
            {/*                                    </button>*/}
            {/*                                </div>*/}
            {/*                            </div>*/}
            {/*                        </>*/}
            {/*                    )}*/}
            {/*                </div>*/}

            {/*                /!* Paramètres *!/*/}
            {/*                <button className="p-2 rounded-lg hover:bg-gray-100">*/}
            {/*                    <Settings className="h-5 w-5 text-gray-600" />*/}
            {/*                </button>*/}

            {/*                /!* Avatar utilisateur *!/*/}
            {/*                <div className="flex items-center">*/}
            {/*                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-medium">*/}
            {/*                        AS*/}
            {/*                    </div>*/}
            {/*                </div>*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</nav>*/}

            {/* Contenu principal */}
            <div className="flex">
                {/* Contenu principal */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-4 md:p-6">
                        {/* En-tête avec stats globales */}
                        <div className="mb-8">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                                        Dashboard de Surveillance
                                    </h1>
                                    <p className="text-gray-600 mt-2">
                                        Surveillez vos sites en temps réel
                                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                                            {globalStats.totalSites} site{globalStats.totalSites !== 1 ? 's' : ''}
                                        </span>
                                    </p>
                                </div>

                                <div className="flex items-center space-x-3">
                                    {/* Filtre temps mobile */}
                                    <div className="md:hidden">
                                        <select
                                            value={timeFilter}
                                            onChange={(e) => setTimeFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        >
                                            <option value="today">Aujourd'hui</option>
                                            <option value="week">Cette semaine</option>
                                            <option value="month">Ce mois</option>
                                        </select>
                                    </div>

                                    <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                        <Download className="h-4 w-4 mr-2" />
                                        Exporter
                                    </button>
                                    <button className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                                        <Upload className="h-4 w-4 mr-2" />
                                        Importer
                                    </button>
                                </div>
                            </div>

                            {/* Stats rapides */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-blue-700 mb-1">Sites actifs</p>
                                            <p className="text-2xl font-bold text-blue-900">
                                                {globalStats.activeSites}/{globalStats.totalSites}
                                            </p>
                                        </div>
                                        <Globe className="h-8 w-8 text-blue-600 opacity-70" />
                                    </div>
                                    <p className="text-xs text-blue-600 mt-2">
                                        {globalStats.totalSites > 0
                                            ? `${((globalStats.activeSites / globalStats.totalSites) * 100).toFixed(1)}% de disponibilité`
                                            : 'Aucun site'}
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-green-700 mb-1">En ligne maintenant</p>
                                            <p className="text-2xl font-bold text-green-900">
                                                {globalStats.onlineNow}
                                            </p>
                                        </div>
                                        <Users className="h-8 w-8 text-green-600 opacity-70" />
                                    </div>
                                    <p className="text-xs text-green-600 mt-2">
                                        Visiteurs simultanés
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-purple-700 mb-1">Visites aujourd'hui</p>
                                            <p className="text-2xl font-bold text-purple-900">
                                                {globalStats.todayVisits.toLocaleString()}
                                            </p>
                                        </div>
                                        <Activity className="h-8 w-8 text-purple-600 opacity-70" />
                                    </div>
                                    <p className="text-xs text-purple-600 mt-2">
                                        En temps réel
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-orange-700 mb-1">Uptime moyen</p>
                                            <p className="text-2xl font-bold text-orange-900">
                                                {globalStats.avgUptime.toFixed(1)}%
                                            </p>
                                        </div>
                                        <BarChart3 className="h-8 w-8 text-orange-600 opacity-70" />
                                    </div>
                                    <p className="text-xs text-orange-600 mt-2">
                                        30 derniers jours
                                    </p>
                                </div>
                            </div>

                            {/* Recherche mobile */}
                            <div className="md:hidden mb-6">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        id="mobile-search"
                                        type="text"
                                        placeholder="Rechercher un site..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                    />
                                </div>
                            </div>

                            {/* Alertes */}
                            {error && (
                                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center">
                                        <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                                        <p className="text-red-800">
                                            {error}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {sites && sites.length === 0 && !loading && (
                                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center">
                                        <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                                        <p className="text-yellow-800">
                                            Aucun site à surveiller. Ajoutez des sites dans la configuration.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contenu principal */}
                        <div className="space-y-8">
                            {/* Widget rapide */}
                            <QuickActions />

                            {/* Graphiques */}
                            <SiteCharts />

                            {/* Statistiques détaillées */}
                            <DashboardStats />

                            {/* Widget statut des sites */}
                            <SiteStatusWidget sites={filteredSites} />

                            {/* Tableau des sites */}
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        Sites Surveillés
                                    </h2>
                                    <div className="flex items-center space-x-3">
                                        {/* Filtre temps desktop */}
                                        <div className="hidden md:flex items-center border border-gray-300 rounded-lg">
                                            <button
                                                onClick={() => setTimeFilter('today')}
                                                className={`px-3 py-1.5 text-sm ${timeFilter === 'today' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                                            >
                                                Aujourd'hui
                                            </button>
                                            <button
                                                onClick={() => setTimeFilter('week')}
                                                className={`px-3 py-1.5 text-sm ${timeFilter === 'week' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                                            >
                                                Semaine
                                            </button>
                                            <button
                                                onClick={() => setTimeFilter('month')}
                                                className={`px-3 py-1.5 text-sm ${timeFilter === 'month' ? 'bg-blue-50 text-blue-700' : 'text-gray-600'}`}
                                            >
                                                Mois
                                            </button>
                                        </div>

                                        <span className="text-sm text-gray-500">
                                            {filteredSites.length} site{filteredSites.length !== 1 ? 's' : ''} affiché{filteredSites.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                                <SitesTable />
                            </div>

                            {/* Activité récente */}
                            <RecentActivity />
                        </div>

                        {/* Pied de page */}
                        <div className="mt-12 pt-8 border-t border-gray-200">
                            <div className="flex flex-col md:flex-row md:items-center justify-between text-sm text-gray-500">
                                <div>
                                    <p>© {new Date().getFullYear()} SiteMonitor. Tous droits réservés.</p>
                                    <p className="mt-1">Version 1.0.0</p>
                                </div>
                                <div className="flex items-center space-x-4 mt-4 md:mt-0">
                                    <button className="hover:text-gray-700">Aide</button>
                                    <button className="hover:text-gray-700">Documentation</button>
                                    <button className="hover:text-gray-700">API</button>
                                    <button className="hover:text-gray-700">Contact</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Widgets flottants */}
            <SocketStatus />
        </div>
    );
}
